/**
 * Zitadel OIDC PKCE client for HROS.
 *
 * Mirrors apps/desktop/src/lib/zitadel-oidc.ts (Laabha) — see that file for
 * detailed comments. The only differences here are the OIDC client_id
 * (`hros-dev` instead of `laabha-dev`) and the redirect/post-logout URIs
 * which target hros.axonbos.com.
 *
 * The Zitadel session is shared across all AxonBOS apps registered in the
 * same Zitadel instance — once the user signs in via Laabha or YesBOS web,
 * HROS will reuse the existing session and skip the password prompt.
 */
export const ZITADEL_ISSUER = 'https://dev-auth.axonbos.com';
export const HROS_CLIENT_ID = '372807744090079243';

export function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

export function postLogoutRedirectUri(): string {
  return `${window.location.origin}/`;
}

const SCOPE =
  'openid profile email offline_access urn:zitadel:iam:org:project:id:zitadel:aud';

const TOKEN_KEY = 'axon_token';
const REFRESH_TOKEN_KEY = 'axon_refresh_token';
const USER_KEY = 'axon_user';
const COMPANY_ID_KEY = 'axon_company_id';
const ID_TOKEN_KEY = 'axon_id_token';

function base64urlEncode(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.length; i += 1) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomB64Url(byteLength: number): string {
  const buf = new Uint8Array(byteLength);
  crypto.getRandomValues(buf);
  return base64urlEncode(buf.buffer);
}

async function sha256B64Url(s: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return base64urlEncode(digest);
}

export async function startZitadelLogin(prompt: 'login' | 'create' = 'login'): Promise<void> {
  const verifier = randomB64Url(32);
  const challenge = await sha256B64Url(verifier);
  const state = randomB64Url(16);
  const nonce = randomB64Url(16);

  sessionStorage.setItem('pkce_verifier', verifier);
  sessionStorage.setItem('oidc_state', state);
  sessionStorage.setItem('oidc_nonce', nonce);

  const params = new URLSearchParams({
    client_id: HROS_CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: SCOPE,
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    prompt,
  });
  window.location.assign(`${ZITADEL_ISSUER}/oauth/v2/authorize?${params.toString()}`);
}

interface ZitadelTokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

interface ZitadelUserInfo {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  preferred_username?: string;
}

export async function handleZitadelCallback(searchParams: URLSearchParams): Promise<void> {
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const err = searchParams.get('error');
  const errDesc = searchParams.get('error_description');
  if (err) throw new Error(`Zitadel error: ${err}${errDesc ? ` — ${errDesc}` : ''}`);
  if (!code) throw new Error('Missing auth code');
  const expectedState = sessionStorage.getItem('oidc_state');
  if (!state || state !== expectedState) throw new Error('Invalid state — possible CSRF replay');
  const verifier = sessionStorage.getItem('pkce_verifier');
  if (!verifier) throw new Error('Missing PKCE verifier — restart the flow');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    client_id: HROS_CLIENT_ID,
    code_verifier: verifier,
  });
  const tokenResp = await fetch(`${ZITADEL_ISSUER}/oauth/v2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!tokenResp.ok) {
    throw new Error(`Token exchange failed (HTTP ${tokenResp.status}): ${await tokenResp.text()}`);
  }
  const tokens = (await tokenResp.json()) as ZitadelTokenResponse;

  const userResp = await fetch(`${ZITADEL_ISSUER}/oidc/v1/userinfo`, {
    headers: { Authorization: `Bearer ${tokens.access_token}` },
  });
  if (!userResp.ok) throw new Error(`Userinfo failed (HTTP ${userResp.status})`);
  const u = (await userResp.json()) as ZitadelUserInfo;

  localStorage.setItem(TOKEN_KEY, tokens.access_token);
  if (tokens.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  if (tokens.id_token) localStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
  localStorage.setItem(
    USER_KEY,
    JSON.stringify({
      id: u.sub,
      email: u.email || '',
      full_name: u.name || u.preferred_username || '',
    }),
  );

  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('oidc_state');
  sessionStorage.removeItem('oidc_nonce');
}

export function startZitadelLogout(): void {
  const idToken = localStorage.getItem(ID_TOKEN_KEY) || '';
  [TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY, COMPANY_ID_KEY, ID_TOKEN_KEY].forEach((k) =>
    localStorage.removeItem(k),
  );
  const params = new URLSearchParams({
    post_logout_redirect_uri: postLogoutRedirectUri(),
    client_id: HROS_CLIENT_ID,
  });
  if (idToken) params.set('id_token_hint', idToken);
  window.location.assign(`${ZITADEL_ISSUER}/oidc/v1/end_session?${params.toString()}`);
}
